/**
 * @file Date and time related helpers.
 */

/**
 * Compares a given ISO datetime string to the current date and time.
 *
 * @param dateTimeString - The ISO datetime string to compare.
 * @returns A string indicating whether the given datetime is "before", "after", or "same" as the current datetime.
 *
 * - "before": The input datetime is after the current datetime.
 * - "after": The input datetime is before the current datetime.
 * - "same": The input datetime is exactly the same as the current datetime.
 *
 * Returns "after" for undefined/null/empty/invalid inputs (assumes event has passed).
 */
export function compareDatetimeToNowISO(dateTimeString: string): "before" | "after" | "same" {
  // Handle undefined/null/empty input - treat as "after" (event has passed)
  if (!dateTimeString) {
    return "after";
  }

  const inputDate = new Date(dateTimeString);
  const inputDateTime = inputDate.getTime();

  // Handle invalid date strings
  if (isNaN(inputDateTime)) {
    return "after";
  }

  const currentDate = new Date().getTime();

  if (currentDate < inputDateTime) {
    return "before";
  } else if (currentDate > inputDateTime) {
    return "after";
  } else {
    return "same";
  }
}

/**
 * Checks if the current datetime is after or the same as the given ISO datetime string.
 *
 * @param dateTimeString - The datetime string to compare in ISO format.
 * @returns `true` if the currrent datetime is after or the same as the provided datetime, otherwise `false`.
 */
export function isAfterDatetime(dateTimeString: string): boolean {
  const result = compareDatetimeToNowISO(dateTimeString);
  return ["after", "same"].includes(result);
}

/**
 * Checks if the current datetime is strictly before the given ISO datetime string.
 *
 * @param dateTimeString - The datetime string to compare in ISO format.
 * @returns `true` if the current datetime is before the provided datetime, otherwise `false`.
 */
export function isBeforeDatetime(dateTimeString: string): boolean {
  const result = compareDatetimeToNowISO(dateTimeString);
  return ["before"].includes(result);
}

/**
 * An object containing arrays of month names in both full and short formats.
 *
 * @property {string[]} full - An array of full month names.
 * @property {string[]} short - An array of abbreviated month names.
 */
export const monthNames = {
  full: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ],
  short: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
};

/**
 * Parses a date string and returns an array containing the year, month, and day.
 * Optionally, the month can be returned as a full name.
 *
 * @param {string} dateStr - The date string to parse, in the format 2023-01-01T00:00:00.
 * @param {boolean} [displayMonthName=false] - Whether to display the month as a full name.
 * @returns {[number, string | number, number]} An array containing the year, month (as a number or full name), and day.
 */
export const parseDateParts = (
  dateStr: string = "",
  displayMonthName: boolean = false
): [number, string | number, number] => {
  // Ensure the date string has a time component to avoid timezone issues.
  // When called without a time component, the Date constructor assumes we want midnight (00:00:00) UTC,
  // whereas if we include a time component, it will use the local timezone.
  const normalizedDateStr = dateStr.match(/^\d\d\d\d-\d\d-\d\d$/) ? `${dateStr}T00:00:00` : dateStr;

  const date = new Date(normalizedDateStr);
  return [
    date.getFullYear(),
    (displayMonthName && monthNames.full[date.getMonth()]) || date.getMonth() + 1,
    date.getDate()
  ];
};
